import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { useLang } from "@/contexts/LanguageContext";
import { getPost, posts } from "@/lib/blog-data";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  component: BlogPostPage,
});

function BlogPostPage() {
  const { post } = Route.useLoaderData();
  const { lang, t } = useLang();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <article className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link
            to="/"
            hash="blog"
            className="inline-flex items-center gap-2 text-[#00C853] font-semibold mb-8 hover:gap-3 transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> {t.blog.back}
          </Link>

          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5 bg-[#00C853]/10 text-[#00C853] px-3 py-1 rounded-full font-semibold">
              <Tag className="w-3 h-3" /> {post.tag[lang]}
            </span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {post.date[lang]}</span>
          </div>

          <h1 className="mt-5 text-4xl lg:text-5xl font-bold text-[#0E1B2C] tracking-tight leading-tight">
            {post.title[lang]}
          </h1>

          <img src={post.cover} alt={post.coverAlt[lang]} className="mt-8 w-full aspect-[16/9] object-cover rounded-2xl shadow-lg" />

          <div className="mt-10 prose prose-slate max-w-none">
            {post.content[lang].map((para: string, i: number) => (
              <p key={i} className="text-slate-700 text-lg leading-relaxed mt-5">{para}</p>
            ))}
          </div>

          {post.sources.length > 0 && (
            <aside className="mt-10 border-t border-slate-200 pt-6" aria-label={lang === "es" ? "Fuentes" : "Sources"}>
              <h2 className="text-lg font-bold text-[#0E1B2C]">
                {lang === "es" ? "Fuentes consultadas" : "Sources"}
              </h2>
              <ul className="mt-3 space-y-2">
                {post.sources.map((source) => (
                  <li key={source.url}>
                    <a href={source.url} target="_blank" rel="noreferrer" className="text-sm text-[#00C853] underline underline-offset-2 hover:text-[#00B248]">
                      {source.label[lang]}
                    </a>
                  </li>
                ))}
              </ul>
            </aside>
          )}

          <div className="mt-14 pt-10 border-t border-slate-200">
            <Link
              to="/"
              hash="blog"
              className="inline-flex items-center gap-2 bg-[#00C853] hover:bg-[#00B248] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> {t.blog.back}
            </Link>
          </div>

          <div className="mt-16">
            <h3 className="text-2xl font-bold text-[#0E1B2C] mb-6">
              {lang === "es" ? "Otros artículos" : "More articles"}
            </h3>
            <div className="grid sm:grid-cols-2 gap-5">
              {posts.filter((p) => p.slug !== post.slug).slice(0, 2).map((p) => (
                <Link
                  key={p.slug}
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="block bg-white rounded-xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg transition-shadow"
                >
                  <img src={p.cover} alt="" className="w-full aspect-[16/10] object-cover" />
                  <div className="p-5">
                    <div className="text-xs text-[#00C853] font-semibold">{p.date[lang]}</div>
                    <div className="mt-1 font-semibold text-[#0E1B2C] line-clamp-2">{p.title[lang]}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
}
